import React, { useMemo } from 'react';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTableRow } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTable } from 'apps/sigma2-ui/src/components/AdminLayout';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { useCallback } from 'react';
import { AddUserToGroupRequest } from 'libs/end-to-end/src/group-entitlements';
import { GetGroupUsersResponse } from 'libs/end-to-end/src/group-entitlements';
import { OrganizationGroupUserSelection } from 'libs/end-to-end/src/group-entitlements';
import { OrganizationGroupSelectionWithOrganization } from 'libs/end-to-end/src/group-entitlements';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useParams } from 'react-router';
import { CircularProgress } from '@mui/material';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { GetOrganizationUsersResponse } from 'libs/end-to-end/src/group-entitlements';
import { AreYouSureDialog } from 'apps/sigma2-ui/src/components/AreYouSureDialog';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { convertTranslatedContentToReactNode } from 'apps/sigma2-ui/src/lib/hooks/useTranslation';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';

export const GroupUsersPage = () => {
  const language = useLanguage();
  const { groupId } = useParams<{ groupId: string }>();
  const { myUser, userAuthenticatedApiCall } = useUser();

  const getGroupUrlFn = useCallback(() => {
    if (!groupId) {
      return null;
    }

    return `${getApiBaseUrl()}/groups/${groupId}`;
  }, [groupId]);

  const groupReadPattern = useSimpleReadPattern({
    getUrlFn: getGroupUrlFn,
    canRead: true,
    responseSchema: OrganizationGroupSelectionWithOrganization,
  });

  const isGroupLoading = useMemo(
    () => groupReadPattern.status === 'loading',
    [groupReadPattern.status],
  );
  const isGroupError = useMemo(
    () => groupReadPattern.status === 'error',
    [groupReadPattern.status],
  );

  const group = useMemo(() => {
    if (groupReadPattern.status !== 'done') {
      return null;
    }

    return groupReadPattern.entity;
  }, [groupReadPattern]);

  const organizationId = useMemo(() => {
    if (!group) {
      return null;
    }

    return group.organizationId;
  }, [group]);

  const getUsersUrlFn = useCallback(() => {
    if (!organizationId) {
      return null;
    }

    return `${getApiBaseUrl()}/organizations/${organizationId}/users`;
  }, [organizationId]);

  const organizationUsersReadPattern = useSimpleReadPattern({
    getUrlFn: getUsersUrlFn,
    canRead: true,
    responseSchema: GetOrganizationUsersResponse,
  });

  const isOrganizationUsersLoading = useMemo(
    () => organizationUsersReadPattern.status === 'loading',
    [organizationUsersReadPattern.status],
  );

  const isOrganizationUsersError = useMemo(
    () => organizationUsersReadPattern.status === 'error',
    [organizationUsersReadPattern.status],
  );

  const organizationUsers = useMemo(() => {
    if (organizationUsersReadPattern.status !== 'done') {
      return [];
    }

    return organizationUsersReadPattern.entity.users;
  }, [organizationUsersReadPattern]);

  const getGroupUsersUrlFn = useCallback(() => {
    if (!groupId) {
      return null;
    }

    return `${getApiBaseUrl()}/groups/${groupId}/users`;
  }, [groupId]);

  const readGroupUsersPattern = useSimpleReadPattern({
    getUrlFn: getGroupUsersUrlFn,
    canRead: true,
    responseSchema: GetGroupUsersResponse,
  });

  const isGroupUsersLoading = useMemo(
    () => readGroupUsersPattern.status === 'loading',
    [readGroupUsersPattern.status],
  );

  const isGroupUsersError = useMemo(
    () => readGroupUsersPattern.status === 'error',
    [readGroupUsersPattern.status],
  );

  const groupUsers = useMemo(() => {
    if (readGroupUsersPattern.status !== 'done') {
      return [];
    }

    return readGroupUsersPattern.entity.users;
  }, [readGroupUsersPattern]);

  const refreshGroupUsers = useCallback(() => {
    if (readGroupUsersPattern.status === 'loading') {
      return;
    }

    readGroupUsersPattern.reloadEntity();
  }, [readGroupUsersPattern]);

  const isLoading = useMemo(
    () => isGroupLoading || isOrganizationUsersLoading || isGroupUsersLoading,
    [isGroupLoading, isOrganizationUsersLoading, isGroupUsersLoading],
  );

  const isError = useMemo(
    () => isGroupError || isOrganizationUsersError || isGroupUsersError,
    [isGroupError, isOrganizationUsersError, isGroupUsersError],
  );

  const [pendingRemoveGroupUserId, setPendingRemoveGroupUserId] =
    React.useState<string | null>(null);

  const [isAddingUserToGroup, setIsAddingUserToGroup] = React.useState(false);
  const [addUserError, setAddUserError] = React.useState<BaseError | null>(
    null,
  );

  const onAddUserToGroup = useAsyncCallback(
    async (isAlive: IsAliveFn, organizationUserId: string) => {
      if (!isAlive() || !groupId || !myUser) {
        return;
      }

      try {
        setIsAddingUserToGroup(true);
        setAddUserError(null);
        const request: AddUserToGroupRequest = {
          organizationUserId,
        };

        const addUserToGroupResult = await userAuthenticatedApiCall({
          url: `${getApiBaseUrl()}/groups/${groupId}/users`,
          requestOptions: {
            method: 'POST',
            body: JSON.stringify(request),
          },
          responseSchema: OrganizationGroupUserSelection,
        });

        if (!isAlive() || !addUserToGroupResult) {
          return;
        }

        if (addUserToGroupResult.resultType === 'err') {
          setAddUserError(addUserToGroupResult.error);
          return;
        }

        refreshGroupUsers();
      } finally {
        if (isAlive()) {
          setIsAddingUserToGroup(false);
        }
      }
    },
    [groupId, myUser, refreshGroupUsers, userAuthenticatedApiCall],
  );

  const [isRemovingUserFromGroup, setIsRemovingUserFromGroup] =
    React.useState(false);
  const [removeUserError, setRemoveUserError] =
    React.useState<BaseError | null>(null);

  const onRemoveUserFromGroup = useAsyncCallback(
    async (isAlive: IsAliveFn, groupUserId: string) => {
      if (!isAlive() || !groupId || !myUser) {
        return;
      }

      try {
        setIsRemovingUserFromGroup(true);
        setRemoveUserError(null);

        const removeUserFromGroupResult = await userAuthenticatedApiCall({
          url: `${getApiBaseUrl()}/groups/${groupId}/users/${groupUserId}`,
          requestOptions: {
            method: 'DELETE',
          },
          responseSchema: EmptyObject,
        });

        if (!isAlive() || !removeUserFromGroupResult) {
          return;
        }

        if (removeUserFromGroupResult.resultType === 'err') {
          setRemoveUserError(removeUserFromGroupResult.error);
          return;
        }

        refreshGroupUsers();
      } finally {
        if (isAlive()) {
          setIsRemovingUserFromGroup(false);
          setPendingRemoveGroupUserId(null);
        }
      }
    },
    [groupId, myUser, refreshGroupUsers, userAuthenticatedApiCall],
  );

  const rows: AdminTableRow[] = useMemo(() => {
    return groupUsers.map((groupUser) => {
      return {
        key: groupUser.id,
        columns: [
          {
            key: 'user',
            columnType: 'text',
            text: groupUser.organizationUser.user.email,
          },
          {
            key: 'actions',
            columnType: 'custom',
            ui: (
              <>
                <Spacing size={'md'} direction={'vertical'}>
                  <Button
                    behavior={{
                      type: 'onClick',
                      onClick: () => {
                        setPendingRemoveGroupUserId(groupUser.id);
                      },
                    }}
                    variant={'destructive'}
                  >
                    {convertTranslatedContentToReactNode(
                      language.removeUserFromGroupButton(),
                    )}
                  </Button>

                  {pendingRemoveGroupUserId !== null &&
                    pendingRemoveGroupUserId === groupUser.id && (
                      <AreYouSureDialog
                        onCancel={() => {
                          setPendingRemoveGroupUserId(null);
                        }}
                        onSure={() => {
                          if (pendingRemoveGroupUserId) {
                            onRemoveUserFromGroup(pendingRemoveGroupUserId);
                          }
                        }}
                        bodyTranslatedContent={language.areYouSureRemoveUserFromGroup(
                          {
                            email:
                              organizationUsers.find(
                                (organizationUser) =>
                                  organizationUser.id ===
                                  groupUser.organizationUser.id,
                              )?.user.email ?? '',
                          },
                        )}
                        disableInputs={isRemovingUserFromGroup}
                      />
                    )}
                </Spacing>
              </>
            ),
          },
        ],
      };
    });
  }, [
    groupUsers,
    isRemovingUserFromGroup,
    onRemoveUserFromGroup,
    organizationUsers,
    pendingRemoveGroupUserId,
    language,
  ]);

  const [pendingOrganizationUserId, setPendingOrganizationUserId] =
    React.useState<string | null>(null);

  const dropdownUsers = useMemo(() => {
    return organizationUsers.filter(
      (organizationUser) =>
        !groupUsers.some(
          (groupUser) =>
            groupUser.organizationUser.user.id === organizationUser.user.id,
        ),
    );
  }, [groupUsers, organizationUsers]);

  return (
    <AdminLayout currentPage={'GroupUsers'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Groups', to: '/groups' },
          {
            label: 'Group Users',
            to: `/groups/${groupId}/users`,
          },
        ]}
      />
      {isLoading && (
        <CircularProgress
          className="text-secondary"
          color="inherit"
          size={96}
          thickness={8}
        />
      )}
      {!isLoading && isError && (
        <Spacing size={'md'} direction={'vertical'}>
          <S2Typography
            variant={'headline-7-alt'}
            translatedContent={language.unknownError()}
          />
        </Spacing>
      )}
      {!isLoading && !isError && (
        <Spacing size={'md'} direction={'vertical'}>
          <S2Typography
            variant={'headline-4'}
            translatedContent={language.groupUsersTitle()}
          />
          <S2Typography
            variant={'body-5'}
            translatedContent={language.groupNameWithLabel({
              groupName: group?.name ?? '',
            })}
          />
          <S2Typography
            variant={'body-5'}
            translatedContent={language.organizationNameWithLabel({
              organizationName: group?.organization.name ?? '',
            })}
          />
          <Spacing size={'md'} direction={'horizontal'}>
            <SelectBoxWithSearch
              type={'single'}
              setSelected={(newValue) => {
                setPendingOrganizationUserId(
                  newValue === 'none' ? null : newValue,
                );
              }}
              selected={pendingOrganizationUserId ?? 'none'}
              list={[
                {
                  optionValue: 'none',
                  optionLabel: 'None',
                },
                ...dropdownUsers.map((organizationUser) => {
                  return {
                    optionValue: organizationUser.id,
                    optionLabel: organizationUser.user.email,
                  };
                }),
              ]}
              label={getTranslatedContentAsString(language.selectUserLabel())}
            />
            <Button
              behavior={{
                type: 'onClick',
                onClick: () => {
                  if (pendingOrganizationUserId) {
                    onAddUserToGroup(pendingOrganizationUserId);
                  }
                },
              }}
              disabled={
                isAddingUserToGroup || pendingOrganizationUserId === null
              }
              variant={'primary'}
            >
              {convertTranslatedContentToReactNode(
                language.addUserToGroupButton(),
              )}
            </Button>
          </Spacing>
          {addUserError && (
            <S2Typography
              variant={'headline-7-alt'}
              translatedContent={language.errorAddingUserToGroup({
                baseError: addUserError,
              })}
            />
          )}
          {removeUserError && (
            <S2Typography
              variant={'headline-7-alt'}
              translatedContent={language.errorRemovingUserFromGroup({
                baseError: removeUserError,
              })}
            />
          )}
          {rows.length > 0 && (
            <AdminTable headings={['User', 'Actions']} rows={rows} />
          )}
          {rows.length === 0 && (
            <S2Typography
              variant={'headline-7-alt'}
              translatedContent={language.noUsersInGroup()}
            />
          )}
          <Button
            behavior={{
              type: 'href',
              href: '/groups',
            }}
            variant={'outline'}
          >
            {convertTranslatedContentToReactNode(language.backToGroupsButton())}
          </Button>
        </Spacing>
      )}
    </AdminLayout>
  );
};
